import { floor, flatten, isArray, isObject, isString, times, zip, first } from 'lodash';
import { Result } from '.';
import { Node, isFieldNode } from './representation';

export enum T {
  string = 'String',
  number = 'Number',
  float = 'Float',
  integer = 'Integer',
  boolean = 'Boolean',
  date = 'Date',
  object = 'Object',
  interval = 'Interval',
  enum = 'Enum',
  reference = 'Reference',
}

export const oneOf = (...values: readonly T[]) => ({ $oneOf: values });

export type OneOfT = { $oneOf: readonly T[] };

export const optional = (value: T | OneOfT) => ({ $optional: value });

export type OptionalT = { $optional: T | OneOfT };

export const variadic = (...values: readonly (T | OneOfT)[]) => ({ $variadic: values });

export type VariadicT = { $variadic: readonly (T | OneOfT)[] };

export const isNumeric = (type: T | null) => type === T.number || type === T.float || type === T.integer;

export const parseT = (type: string | null): T | null => {
  switch (type) {
    case 'String':
      return T.string;
    case 'Number':
      return T.number;
    case 'Float':
      return T.float;
    case 'Integer':
      return T.integer;
    case 'Boolean':
      return T.boolean;
    case 'Date':
      return T.date;
    case 'Interval':
      return T.interval;
    case 'Object':
      return T.object;
    case 'Enum':
      return T.enum;
    case 'Reference':
      return T.reference;
    case null:
      return null;
    default:
      throw new Error(`Unknown type ${type}`);
  }
};

export const DurationUnits = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second', 'millisecond'];

// Units supported by "date_diff" function.
export const DateDiffUnits = ['year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second'];

export type FunctionArgument = {
  name: string;
  description?: string;
  values?: readonly string[];
  optional?: boolean;
};

// Null as returnType means "unknown" type.
export type FunctionSignature = {
  arguments: readonly [...(T | OneOfT | VariadicT | OptionalT)[]];
  returnType: T | null;
};

export type FunctionDefinition = {
  name: string;
  description: string;
  operator?: string;
  signatures: readonly FunctionSignature[];
  documentation: {
    section: string;
  };
  arguments?: readonly (FunctionArgument | { variadic: FunctionArgument[] })[];
};

export type FunctionDefinitions = { [key: string]: FunctionDefinition };

export const isOneOfT = (value: unknown): value is OneOfT =>
  isObject(value) && '$oneOf' in value && isArray(value.$oneOf);

export const isOptionalT = (value: unknown): value is OptionalT =>
  isObject(value) && '$optional' in value && (isString(value.$optional) || isOneOfT(value.$optional));

export const isVariadic = (value: unknown): value is VariadicT =>
  isObject(value) && '$variadic' in value && isArray(value.$variadic);

const matchTypes = (received: T | null, expected: T | OneOfT | OptionalT | null): boolean => {
  if (isOptionalT(expected) && isOneOfT(expected.$optional)) {
    return matchTypes(received, expected.$optional);
  }
  if (isOptionalT(expected)) {
    return received === expected.$optional || received === null;
  }
  if (isOneOfT(expected)) {
    return expected.$oneOf.some((type) => matchTypes(received, type));
  }

  return received === expected || received === null || expected === null;
};

const variadicArguments = (length: number, variadicArguments: readonly [...(T | OneOfT)[]]) => {
  const needLength = floor(length / variadicArguments.length);
  return flatten(times(needLength, () => variadicArguments));
};

export type ArgumentTypeMatch = {
  matchingType: boolean;
  expected: T | OneOfT | OptionalT | null;
  received: T | null;
  node?: Node;
};

const matchArgumentTypes = (
  args: readonly Result[],
  expectedTypes: readonly (T | OneOfT | OptionalT | null)[],
): ArgumentTypeMatch[] =>
  zip(args, expectedTypes)
    .map(([argument, expected]) => {
      if (isOptionalT(expected) && argument?.returnType === undefined) {
        return { argument, matchingType: true, expected };
      }
      if (expected === undefined || argument?.returnType === undefined) {
        return { argument, matchingType: false, expected: expected ?? null };
      }
      if (expected === T.reference && isFieldNode(argument.node)) {
        return { argument, matchingType: true, expected };
      }

      return { argument, matchingType: matchTypes(argument.returnType, expected), expected };
    })
    .map(({ argument, matchingType, expected }) => ({
      node: argument?.node,
      matchingType,
      expected,
      received: argument?.returnType ?? null,
    }));

const argumentsForSignature = (signature: FunctionSignature, length: number) =>
  flatten(
    signature.arguments.map((argument) =>
      isVariadic(argument) ? variadicArguments(length, argument.$variadic) : argument,
    ),
  );

type MatchingSignaturesResult =
  | { type: 'success'; signatures: FunctionSignature[] }
  | { type: 'failure'; closestMatch?: { arguments: ArgumentTypeMatch[]; signature: FunctionSignature } };

export const findMatchingSignatures = (args: Result[], def: FunctionDefinition): MatchingSignaturesResult => {
  const matches = def.signatures
    .map((signature) => ({
      arguments: matchArgumentTypes(args, argumentsForSignature(signature, args.length)),
      signature,
    }))
    // Sort closest matching signatures first -- the ones having more matching types starting the beginning of arguments
    // are preferred. For example [true, true, false] is preferred over [true, false, true] even though they have the
    // same number of matching types.
    .sort((first, second) => {
      const a = first.arguments.map(({ matchingType }) => matchingType);
      const b = second.arguments.map(({ matchingType }) => matchingType);

      const shortest = Math.min(a.length, b.length);

      for (let i = 0; i < shortest; i++) {
        if (a[i] && !b[i]) {
          return -1;
        }
        if (!a[i] && b[i]) {
          return 1;
        }
      }

      return 0;
    });

  const signatures = matches.filter((m) => m.arguments.every((r) => r.matchingType)).map((m) => m.signature);
  if (signatures.length === 0) {
    return { type: 'failure', closestMatch: first(matches) };
  }

  return { type: 'success', signatures };
};
